import { useMemo } from "react"
import {
	CommonGraphStyle,
	selectedItemStyle,
	filter
} from "../../common"
import { useFunnelGraphOption } from "./options"
import { DashBoardConditions } from "features/dashBoard/types"
import { round } from "shared/util"
import { DownLoadData } from "components/download/csv/types"

interface FunnelData {
  code: string
  name: string
  value: number
  percentage: number
  itemStyle?: CommonGraphStyle
}

type useFunnelGraphModelProps<T> = {
	dataList: T[] | null
	conditions: DashBoardConditions,
	filterText: string | undefined
	graphOption: ReturnType<typeof useFunnelGraphOption<T>>
}
export const useFunnelGraphModel = <T,>({
	dataList,
	conditions,
	filterText,
	graphOption: {
		configure,
		targetList,
		axisDef,
		valueDef,
		inverse
	}
}: useFunnelGraphModelProps<T>) => useMemo(
	() => {
		if (!dataList || dataList.length === 0) return null
		if (!axisDef?.key || !valueDef?.key) return null

		const condition = axisDef.key ? conditions[axisDef.key] : undefined
		const data: FunnelData[] = []
		for (const record of dataList) {
			const axis = axisDef.val(record)
			if (!axis) continue

			const value = valueDef.val(record)
			if (value === undefined || value === 0 || !filter(record, configure, conditions, axisDef.key, targetList)) continue

			const axisList = Array.isArray(axis) ? axis : [axis]
			for (const ax of axisList) {
				if (filterText && ax.name) {
					const axName = ax.name
					if (filterText.split(' ').every((t) => axName.indexOf(t) < 0)) continue
				}
				let it = data.find((r) => r.code === ax.code)
				if (!it) {
					it = {
						code: ax.code,
						name: ax.name ?? '',
						value: 0,
						percentage: 0,
						itemStyle: selectedItemStyle(condition, ax)
					}
					data.push(it)
				}
				it.value = it.value + value
			}
		}
		const total = data.reduce((total, r) => total + r.value, 0)
		if (inverse) {
			data.sort((a, b) => a.code.localeCompare(b.code))
		} else {
			data.sort((a, b) => b.code.localeCompare(a.code))
		}
		let n = 0
		data.forEach((r) => {
			n = n + r.value
			r.percentage = n / total
		})
		data.reverse()
		return {
			data,
			total,
			csvBuilder: (): DownLoadData | undefined => {
				if (!data || !axisDef?.key || !valueDef?.key) return undefined
				const axisKey = axisDef.key
				const valueKey = valueDef.key
				return {
					header: [{
						title: axisDef.title,
						key: axisDef.key
					}, {
						title: valueDef.title,
						key: valueDef.key,
						unit: valueDef.unit
					}, {
						title: '割合',
						key: 'percentage',
						unit: '%'
					}],
					body: data.map((data) => ({
						[axisKey]: data.name,
						[valueKey]: data.value,
						percentage: round(data.percentage, 1)
					})) || []
				}
			}
		}
	},
	[dataList, axisDef, valueDef, inverse, filterText, conditions, configure, targetList]
)